import React, { useCallback, useEffect, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Plus, Search, Loader2, Receipt as ReceiptIcon, X, Paperclip } from 'lucide-react';
import { expensesApi, projectsApi } from '../api';
import { ExpenseFormDialog } from './ExpenseFormDialog';
import { fmtDate, centsToDollarString } from '../constants';
import {
  EXPENSE_TYPE_OPTIONS, PAYMENT_METHOD_OPTIONS, EXPENSE_SORT_OPTIONS,
  expenseTypeLabel, paymentMethodLabel,
} from '../expenseConstants';

const selectCls =
  'bg-[#1A1A1A] border border-gray-700 text-white rounded-md px-3 py-2 text-sm focus:border-[#D4AF37] focus:outline-none';

export const ExpensesView = ({ lockedProjectId, projectArchived = false, autoOpenCreate = false, onChanged }) => {
  const locked = lockedProjectId !== undefined && lockedProjectId !== null;
  const [expenses, setExpenses] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const [search, setSearch] = useState('');
  const [projectFilter, setProjectFilter] = useState('all');
  const [expenseType, setExpenseType] = useState('');
  const [workCategory, setWorkCategory] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [archived, setArchived] = useState(false);
  const [sort, setSort] = useState('date_newest');

  const hasFilters = search || expenseType || workCategory || paymentMethod || startDate || endDate || archived || (!locked && projectFilter !== 'all');

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const params = { archived, sort };
      if (search.trim()) params.search = search.trim();
      if (expenseType) params.expense_type = expenseType;
      if (workCategory.trim()) params.work_category = workCategory.trim();
      if (paymentMethod) params.payment_method = paymentMethod;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (locked) params.project_id = lockedProjectId;
      else if (projectFilter === 'general') params.general_expenses_only = true;
      else if (projectFilter !== 'all') params.project_id = projectFilter;
      const res = await expensesApi.list(params);
      setExpenses(res.data);
    } catch { setError(true); } finally { setLoading(false); }
  }, [search, expenseType, workCategory, paymentMethod, startDate, endDate, archived, sort, projectFilter, locked, lockedProjectId]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  useEffect(() => {
    if (!locked) projectsApi.list({ archived: false, sort: 'name_az' }).then((r) => setProjects(r.data)).catch(() => {});
  }, [locked]);

  useEffect(() => {
    if (autoOpenCreate && !projectArchived) { setEditing(null); setDialogOpen(true); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenCreate]);

  const clearFilters = () => {
    setSearch(''); setExpenseType(''); setWorkCategory(''); setPaymentMethod('');
    setStartDate(''); setEndDate(''); setArchived(false); setSort('date_newest');
    if (!locked) setProjectFilter('all');
  };

  const openEdit = (x) => { setEditing(x); setDialogOpen(true); };
  const handleSaved = () => { load(); onChanged?.(); };

  const nameCell = (x) => x.vendor_name || x.description || expenseTypeLabel(x.expense_type);

  return (
    <div data-testid="expenses-view">
      <div className="flex flex-wrap gap-2 mb-5 items-center">
        <div className="relative flex-1 min-w-[180px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search expenses..." className="bg-[#1A1A1A] border-gray-700 text-white focus:border-[#D4AF37] pl-9" data-testid="expenses-search" />
        </div>
        {!locked && (
          <select value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)} className={selectCls} data-testid="expenses-filter-project">
            <option value="all">All Expenses</option>
            <option value="general">General BuildBroz Expenses</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        )}
        <select value={expenseType} onChange={(e) => setExpenseType(e.target.value)} className={selectCls} data-testid="expenses-filter-type">
          <option value="">All Types</option>
          {EXPENSE_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <Input value={workCategory} onChange={(e) => setWorkCategory(e.target.value)} placeholder="Work category" className="bg-[#1A1A1A] border-gray-700 text-white focus:border-[#D4AF37] w-36" data-testid="expenses-filter-category" />
        <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} className={selectCls} data-testid="expenses-filter-payment">
          <option value="">All Payment Methods</option>
          {PAYMENT_METHOD_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="bg-[#1A1A1A] border-gray-700 text-white w-36" data-testid="expenses-filter-start" />
        <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="bg-[#1A1A1A] border-gray-700 text-white w-36" data-testid="expenses-filter-end" />
        <select value={sort} onChange={(e) => setSort(e.target.value)} className={selectCls} data-testid="expenses-sort">
          {EXPENSE_SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm text-gray-300 px-1 cursor-pointer">
          <input type="checkbox" checked={archived} onChange={(e) => setArchived(e.target.checked)} className="accent-[#D4AF37] h-4 w-4" data-testid="expenses-filter-archived" />
          Archived
        </label>
        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="text-gray-400 hover:text-white" data-testid="expenses-clear-filters"><X size={14} /> Clear Filters</Button>
        )}
        <div className="ml-auto">
          {projectArchived ? (
            <span className="text-xs text-gray-500">This project is archived — new expenses cannot be added.</span>
          ) : (
            <Button onClick={() => { setEditing(null); setDialogOpen(true); }} className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold" data-testid="expenses-new-btn">
              <Plus size={16} /> Add Expense
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16 text-gray-500" data-testid="expenses-loading">
          <Loader2 size={26} className="mx-auto mb-2 animate-spin text-[#D4AF37]" /> Loading expenses…
        </div>
      ) : error ? (
        <Card className="bg-[#111111] border-gray-800 p-8 text-center" data-testid="expenses-error">
          <p className="text-red-400 mb-3">Could not load expenses.</p>
          <Button onClick={load} variant="outline" className="border-gray-700 text-gray-300">Retry</Button>
        </Card>
      ) : expenses.length === 0 ? (
        <Card className="bg-[#111111] border-gray-800 p-12 text-center" data-testid="expenses-empty">
          <div className="bg-[#1F1F1F] rounded-2xl p-5 mb-5 inline-block">
            <ReceiptIcon size={36} className="text-[#D4AF37]" />
          </div>
          <h3 className="text-xl font-semibold text-white mb-2">
            {hasFilters ? 'No expenses match these filters.' : 'No expenses yet.'}
          </h3>
          <p className="text-gray-500 max-w-md mx-auto mb-6">
            {hasFilters ? 'Try adjusting your search or filters.' : 'Record your first BuildBroz purchase or upload a receipt to organize later.'}
          </p>
          {hasFilters ? (
            <Button onClick={clearFilters} variant="outline" className="border-gray-700 text-gray-300">Clear Filters</Button>
          ) : !projectArchived ? (
            <Button onClick={() => { setEditing(null); setDialogOpen(true); }} className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold" data-testid="expenses-empty-create">
              <Plus size={16} /> Add Expense
            </Button>
          ) : null}
        </Card>
      ) : (
        <>
          <Card className="bg-[#111111] border-gray-800 overflow-hidden hidden md:block">
            <table className="w-full text-sm" data-testid="expenses-table">
              <thead>
                <tr className="text-left text-[#D4AF37] text-[10px] uppercase tracking-wider font-brand border-b border-[#D4AF37]/30">
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3 font-medium">Vendor / Description</th>
                  {!locked && <th className="px-4 py-3 font-medium">Project</th>}
                  <th className="px-4 py-3 font-medium">Type</th>
                  <th className="px-4 py-3 font-medium">Category</th>
                  <th className="px-4 py-3 font-medium">Total</th>
                  <th className="px-4 py-3 font-medium">Receipts</th>
                  <th className="px-4 py-3 font-medium">Payment</th>
                </tr>
              </thead>
              <tbody>
                {expenses.map((x) => (
                  <tr key={x.id} onClick={() => openEdit(x)} className={`border-b border-gray-800/60 hover:bg-[#1A1A1A] cursor-pointer ${x.is_archived ? 'opacity-60' : ''}`} data-testid={`expense-row-${x.id}`}>
                    <td className="px-4 py-3 text-gray-300">{fmtDate(x.expense_date)}</td>
                    <td className="px-4 py-3 text-white font-medium">
                      {nameCell(x)}
                      {x.vendor_name && x.description && <span className="block text-xs text-gray-500 font-normal">{x.description}</span>}
                    </td>
                    {!locked && <td className="px-4 py-3 text-gray-300">{x.project ? x.project.name : 'General'}</td>}
                    <td className="px-4 py-3 text-gray-300">{expenseTypeLabel(x.expense_type)}</td>
                    <td className="px-4 py-3 text-gray-400">{x.work_category || '—'}</td>
                    <td className="px-4 py-3 text-[#D4AF37] font-semibold">{centsToDollarString(x.total_cents)}</td>
                    <td className="px-4 py-3 text-gray-400">
                      {x.receipt_count > 0 ? (
                        <span className="flex items-center gap-1"><Paperclip size={13} /> {x.receipt_count}</span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-400">{x.payment_method ? paymentMethodLabel(x.payment_method) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          <div className="grid grid-cols-1 gap-3 md:hidden">
            {expenses.map((x) => (
              <Card key={x.id} onClick={() => openEdit(x)} className={`bg-[#111111] border-gray-800 p-4 ${x.is_archived ? 'opacity-60' : ''}`} data-testid={`expense-card-${x.id}`}>
                <div className="flex justify-between items-start gap-2">
                  <div className="min-w-0">
                    <div className="font-medium text-white truncate">{nameCell(x)}</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {fmtDate(x.expense_date)} · {x.project ? x.project.name : 'General'} · {expenseTypeLabel(x.expense_type)}
                    </div>
                  </div>
                  <span className="text-[#D4AF37] font-semibold shrink-0">{centsToDollarString(x.total_cents)}</span>
                </div>
                <div className="flex gap-3 mt-2 text-xs text-gray-500">
                  {x.work_category && <span>{x.work_category}</span>}
                  {x.receipt_count > 0 && <span className="flex items-center gap-1"><Paperclip size={11} /> {x.receipt_count}</span>}
                  {x.is_archived && <span className="uppercase text-[10px] border border-gray-600 rounded px-1">Archived</span>}
                </div>
              </Card>
            ))}
          </div>
        </>
      )}

      <ExpenseFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        mode={editing ? 'edit' : 'create'}
        expense={editing}
        lockedProjectId={locked ? lockedProjectId : undefined}
        onSaved={handleSaved}
      />
    </div>
  );
};
